<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import TopbarOrganism from '@/components/organisms/topbar-organism/TopbarOrganism.vue';
  import ButtonAtom from '@/components/atoms/ButtonAtom.vue';
  import { useRouter } from 'vue-router';
  import coachService, { CoachListItem } from '@/services/coach/coach.service';

  const router = useRouter();
  const coaches = ref<CoachListItem[]>([]);
  const loading = ref(false);
  const error = ref('');

  const navigateToCreate = () => {
    router.push({ path: '/coach-create' });
  };

  const navigateToEdit = (coachId: string) => {
    router.push({ path: `/coach-edit/${coachId}` });
  };

  const fetchCoaches = async () => {
    loading.value = true;
    error.value = '';

    try {
      const response = await coachService.getAll();
      coaches.value = response.data || [];
    } catch (err) {
      console.error('Error fetching coaches:', err);
      error.value = 'Failed to fetch coaches. Please try again.';
    } finally {
      loading.value = false;
    }
  };

  onMounted(() => {
    fetchCoaches();
  });
</script>

<template>
  <div class="coaches-list">
    <TopbarOrganism
      linkText="Coaches"
      :showIcon="false"
      primaryButtonText="Add Coach"
      :onPrimaryClick="navigateToCreate"
    />

    <div class="coaches-list-content">
      <div class="container">
        <h2>Coaches Management</h2>
        <p>
          Manage your coaches here. Click "Add Coach" to create a new coach
          profile.
        </p>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <p>Loading coaches...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <p>{{ error }}</p>
          <button @click="fetchCoaches" class="retry-btn">Retry</button>
        </div>

        <!-- Empty State -->
        <div v-else-if="coaches.length === 0" class="empty-state">
          <p>No coaches found. Create your first coach to get started!</p>
        </div>

        <!-- Coaches Grid -->
        <div v-else class="coaches-grid">
          <div
            v-for="coach in coaches"
            :key="coach.coach_id"
            class="coach-card"
          >
            <div class="coach-photo">
              <img
                :src="coach.coach_photo"
                :alt="`${coach.coach_name} ${coach.coach_lastname}`"
              />
            </div>

            <div class="coach-info">
              <h3 class="coach-name">
                {{ coach.coach_name }} {{ coach.coach_lastname }}
              </h3>
              <p class="coach-headline">{{ coach.coach_headline }}</p>

              <div class="coach-stats">
                <div class="stat-item">
                  <span class="stat-label">Experience:</span>
                  <span class="stat-value"
                    >{{ coach.years_of_experience }} years</span
                  >
                </div>
                <div class="stat-item">
                  <span class="stat-label">Rate:</span>
                  <span class="stat-value"
                    >${{ coach.price_per_hour }}/hour</span
                  >
                </div>
                <div class="stat-item">
                  <span class="stat-label">Rating:</span>
                  <span class="stat-value"
                    >{{ coach.average_rating || 'N/A' }} ({{
                      coach.total_reviews
                    }}
                    reviews)</span
                  >
                </div>
              </div>

              <div
                class="coach-specialities"
                v-if="coach.specialities && coach.specialities.length > 0"
              >
                <div class="speciality-tags">
                  <span
                    v-for="speciality in coach.specialities"
                    :key="speciality.speciality_id"
                    class="speciality-tag"
                  >
                    {{ speciality.speciality_name }}
                  </span>
                </div>
              </div>

              <div class="coach-actions">
                <ButtonAtom
                  class="btn btn-secondary btn-small"
                  @click="navigateToEdit(coach.coach_id)"
                  :disabled="false"
                >
                  Edit Coach
                </ButtonAtom>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .coaches-list {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .coaches-list-content {
    flex: 1;
    padding: 24px;
    overflow-y: auto;
    background-color: #fff;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .container h2 {
    margin-bottom: 16px;
    color: #2c3e50;
    font-size: 28px;
    font-weight: 600;
  }

  .container p {
    margin-bottom: 32px;
    color: #6b7280;
    font-size: 16px;
  }

  /* Loading, Error, and Empty States */
  .loading-state,
  .error-state,
  .empty-state {
    padding: 48px;
    text-align: center;
    border: 2px dashed #e1e5e9;
    border-radius: 12px;
    background-color: #fafafa;
  }

  .loading-state p,
  .error-state p,
  .empty-state p {
    color: #6b7280;
    font-size: 16px;
    margin: 0 0 16px 0;
  }

  .retry-btn {
    padding: 8px 16px;
    background-color: #42b983;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
  }

  .retry-btn:hover {
    background-color: #369870;
  }

  /* Coaches Grid */
  .coaches-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 24px;
  }

  .coach-card {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .coach-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .coach-photo {
    text-align: center;
    margin-bottom: 16px;
  }

  .coach-photo img {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #e1e5e9;
  }

  .coach-info h3 {
    margin: 0 0 8px 0;
    color: #2c3e50;
    font-size: 20px;
    font-weight: 600;
    text-align: center;
  }

  .coach-headline {
    color: #6b7280;
    font-size: 14px;
    text-align: center;
    margin: 0 0 20px 0;
    font-style: italic;
  }

  .coach-stats {
    margin-bottom: 16px;
  }

  .stat-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    padding: 8px 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .stat-item:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .stat-label {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
  }

  .stat-value {
    color: #2c3e50;
    font-size: 14px;
    font-weight: 600;
  }

  .coach-specialities {
    margin-top: 16px;
  }

  .speciality-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .speciality-tag {
    background-color: #42b983;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
  }

  .coach-actions {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f3f4f6;
    text-align: center;
  }

  .btn-small {
    padding: 8px 16px;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    .coaches-list-content {
      padding: 12px;
    }

    .coaches-grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .coach-card {
      padding: 20px;
    }

    .container h2 {
      font-size: 24px;
    }

    .container p {
      font-size: 14px;
    }
  }
</style>
